import { getShiftEligibilityErrorMessage } from '@/features/shifts/utils/shiftEligibility'

/**
 * Maps dotted validation field paths to human-readable labels. The backend's
 * class-validator emits messages like "employment.joiningDate must be a valid
 * ISO 8601 date string"; we surface "Joining date" instead of the raw path.
 */
const FIELD_LABELS: Record<string, string> = {
  joiningDate: 'Joining date',
  confirmationDate: 'Confirmation date',
  probationEndDate: 'Probation end date',
  exitDate: 'Exit date',
  workLocation: 'Work location',
  employmentType: 'Employment type',
  baseSalary: 'Base salary',
  hourlyRate: 'Hourly rate',
  compensationType: 'Compensation type',
  firstName: 'First name',
  lastName: 'Last name',
  roleId: 'Role',
  email: 'Email',
  password: 'Password',
}

const labelForField = (field: string): string => {
  if (FIELD_LABELS[field]) return FIELD_LABELS[field]
  // Fall back to de-camelCasing the leaf field, e.g. "someField" -> "Some field".
  const spaced = field.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[._]/g, ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase()
}

/**
 * Rewrites a single NestJS/class-validator message into friendlier prose.
 * Leaves messages that don't look like validator output untouched, so genuine
 * business errors (e.g. "Email already in use") pass through verbatim.
 */
function humanizeValidationMessage(raw: string): string {
  const message = raw.trim()
  // class-validator messages start with the (possibly dotted) property path.
  const match = message.match(/^([a-zA-Z0-9_]+(?:\.[a-zA-Z0-9_]+)*)\s+(.*)$/)
  if (!match) return message

  const [, path, rest] = match
  const leaf = path.split('.').pop() ?? path
  // Only treat this as a validator message when the leaf maps to a known field
  // or the remainder uses validator phrasing — avoids mangling normal sentences.
  const looksLikeValidator =
    leaf in FIELD_LABELS || /^must |^should |should not |is not |must not /i.test(rest)
  if (!looksLikeValidator) return message

  const label = labelForField(leaf)
  const normalized = rest
    .replace(/a valid ISO 8601 date string/i, 'a valid date')
    .replace(/must be a mongodb id/i, 'is not valid')
    .replace(/must be a valid enum value/i, 'has an invalid selection')
    .replace(/must be an email/i, 'must be a valid email address')
    .replace(/must be a number conforming to the specified constraints/i, 'must be a number')
    .replace(/must be longer than or equal to (\d+) characters/i, 'must be at least $1 characters')
    .replace(/must not be empty/i, 'is required')
    .replace(/should not be empty/i, 'is required')

  return `${label} ${normalized}`.replace(/\s+/g, ' ').trim()
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

/** "2026-02" -> "February 2026"; anything else is returned unchanged. */
const formatPeriod = (period: string): string => {
  const match = period.match(/^(\d{4})-(\d{2})$/)
  const name = match ? MONTHS[Number(match[2]) - 1] : undefined
  return match && name ? `${name} ${match[1]}` : period
}

/**
 * Copy for the payroll integrity codes the backend sends with 409s (period
 * locked by a paid payroll, double payment, void of a paid payroll, a slip
 * paid in several currencies) and the missing-FX 400. Returns undefined for any other error.
 */
function payrollIntegrityMessage(data: Record<string, unknown>): string | undefined {
  const periods = Array.isArray(data.periods)
    ? data.periods.filter((p): p is string => typeof p === 'string').map(formatPeriod)
    : []
  const period = typeof data.period === 'string' ? formatPeriod(data.period) : undefined
  switch (data.code) {
    case 'PAYROLL_PERIOD_LOCKED':
      return `Payroll for ${periods.length ? periods.join(', ') : 'this period'} is already paid, so this period is locked. Reverse that payment first if the change is needed.`
    case 'PAYROLL_ALREADY_PAID':
      return `Already paid${period ? ` for ${period}` : ''}. An employee can be paid only once a month; reverse the earlier payment to pay again.`
    case 'PAYROLL_REVERSAL_REQUIRED':
      return 'This payroll is already paid, so it can no longer be voided. Use "Reverse payment" and give a reason.'
    case 'PAYROLL_SLIP_MIXED_CURRENCY':
      return 'This month was paid in more than one currency, so there is no single slip. Ask your payroll team for the slip of each company.'
    case 'PAYROLL_FX_MISSING':
      return typeof data.message === 'string' && data.message
        ? data.message
        : 'An exchange rate is missing for this payment. Add the rate, then pay again.'
    default:
      return undefined
  }
}

/**
 * Extracts a user-facing error message from unknown error types (Error, AxiosError, etc.).
 * NestJS validation errors (dotted field paths, "ISO 8601" jargon) are humanized;
 * plain business messages are passed through unchanged.
 */
export function getErrorMessage(error: unknown): string {
  const data = (error as { response?: { data?: unknown } })?.response?.data as
    | { message?: unknown; errors?: unknown }
    | undefined

  if (data && typeof data === 'object') {
    const payrollMessage = payrollIntegrityMessage(data as Record<string, unknown>)
    if (payrollMessage) return payrollMessage
    // 409 EMPLOYEE_NOT_ELIGIBLE from any shift path (assign, reassign, cover).
    const eligibilityMessage = getShiftEligibilityErrorMessage(error)
    if (eligibilityMessage) return eligibilityMessage
  }

  // NestJS returns `message` as a string OR an array of validation strings.
  if (Array.isArray(data?.message) && data.message.length > 0) {
    const first = data.message.find((m) => typeof m === 'string' && m.length > 0)
    if (typeof first === 'string') return humanizeValidationMessage(first)
  }
  if (typeof data?.message === 'string' && data.message.length > 0) {
    return humanizeValidationMessage(data.message)
  }
  if (Array.isArray(data?.errors) && data.errors.length > 0) {
    const first = data.errors[0]
    if (typeof first === 'string' && first.length > 0) return humanizeValidationMessage(first)
    if (typeof first === 'object' && first !== null) {
      const nestedMessage = (first as { message?: unknown }).message
      if (typeof nestedMessage === 'string' && nestedMessage.length > 0) {
        return humanizeValidationMessage(nestedMessage)
      }
    }
  }
  if (error instanceof Error && error.message.length > 0) return error.message
  if (typeof error === 'string') return error
  return 'Something went wrong'
}
